import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface UserProfileData {
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
  verification_tier: number;
  user_id: string;
}

function maskEmail(email: string): string {
  if (!email) return "";
  const [local, domain] = email.split("@");
  if (!domain) return email;
  if (local.length <= 2) return `${local}***@${domain}`;
  return `${local.slice(0, 2)}***@${domain}`;
}

export function useUserProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["user_profile_shared", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("user_profiles")
        .select("full_name, avatar_url, email, verification_tier, user_id")
        .eq("user_id", user.id)
        .single();
      if (error) throw error;
      return data as UserProfileData;
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const userEmail = user?.email || query.data?.email || "";
  const maskedEmail = maskEmail(userEmail);
  const initials = userEmail ? userEmail.substring(0, 2).toUpperCase() : "U";
  const displayName = query.data?.full_name || (userEmail ? userEmail.split("@")[0] : "User");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["user_profile_shared", user?.id] });

  return {
    profile: query.data,
    isLoading: query.isLoading,
    avatarUrl: query.data?.avatar_url || null,
    maskedEmail,
    initials,
    displayName,
    verificationTier: query.data?.verification_tier ?? 0,
    invalidateProfile: invalidate,
  };
}
